import * as THREE from 'three';
import BaseModel3d from '../BaseModel3d';
import { globalStores } from '@/editor/stores';
import { pixelRatio3d } from '@/editor/math/const';
import { IBaseDataArcEdgeFace, IFaceData } from '@/editor/data/face/face';
import { EEntityType } from '@/editor/types';
import { EContainerType } from '@/editor/types/entity';
import { createMaterial } from '@/editor/data/material/utils';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { booleanPolygon, BooleanPolygonType, booleanPolygonUnion } from '@/editor/model/polygon';

export class ArcEdgeFaceModel extends BaseModel3d {
  private _planeMesh?: THREE.Mesh;

  constructor(options: { id: string }) {
    super({ id: options.id });

    this.model.userData.type = EEntityType.Face;
    this.model.userData.containerType = EContainerType.entity;
    this.model.userData.entityId = this._id;

    globalStores.editorStateStore.sceneInfo.scene3d.intersectableGroup.add(this.model);
  }

  createModel(faceData: IFaceData) {
    if (!this.canUpdate) return;

    const mesh = this.createPlane(faceData);
    if (!mesh) return;

    this._planeMesh = mesh;
    this.model.add(mesh);

    this.updateWithoutDestroy(faceData);
  }

  private createPlane(faceData: IFaceData): THREE.Mesh | undefined {
    const { start, end, height, offGround, center, clockwise } = faceData.baseData as IBaseDataArcEdgeFace;
    const holes = faceData.holes;

    const arc = new ArcSeg2d({ start, end, center, clockwise });
    const segCount = arc.getDefaulSubdivisionAmount();
    const arcLength = arc.length;

    // 把弧形面在 u 方向 (弧长方向) 切成 segCount 个细分矩形 (strip)
    // - 没有 hole 经过的 strip: 直接 2 三角形
    // - 有 hole 经过的 strip:   strip - holes 布尔差集 -> ShapeGeometry, 再把顶点弯到弧上
    const stripWidth = arcLength / segCount;
    const bottomZ = offGround;
    const topZ = offGround + height;

    // holes 投影到展开 (u, v) 平面: u ∈ [0, arcLength], v 为高度
    const holesUV: IPoint2d[][] =
      holes.length > 0
        ? booleanPolygonUnion(holes.map((hole) => hole.map((p) => ({ x: p.ratioX * arcLength, y: p.y }))))
        : [];

    // 缓存每个 hole 的 u 范围, 用于快速跳过和它无重叠的 strip
    const holeURanges = holesUV.map((hole) => {
      let minU = Infinity;
      let maxU = -Infinity;
      for (const p of hole) {
        if (p.x < minU) minU = p.x;
        if (p.x > maxU) maxU = p.x;
      }
      return { minU, maxU };
    });

    const positions: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];

    // 展开 (u, v) -> 3D 弧上 world 坐标, 同时写入 uv attribute
    const pushVertex = (u: number, v: number) => {
      const ratio = Math.min(1, Math.max(0, u / arcLength));
      const arcPt = arc.getPointFromRatio(ratio);
      positions.push(arcPt.x / pixelRatio3d, arcPt.y / pixelRatio3d, v / pixelRatio3d);
      uvs.push(u / pixelRatio3d, v / pixelRatio3d);
    };

    // 简单矩形 strip: 2 三角形, cylinderFaceModel 同款
    const appendSimpleStrip = (u0: number, u1: number) => {
      const baseIdx = positions.length / 3;
      pushVertex(u0, bottomZ); // 0 lb
      pushVertex(u1, bottomZ); // 1 rb
      pushVertex(u1, topZ); //    2 rt
      pushVertex(u0, topZ); //    3 lt
      // 翻转 winding: 法线指向弧外侧
      indices.push(baseIdx, baseIdx + 2, baseIdx + 1, baseIdx, baseIdx + 3, baseIdx + 2);
    };

    // 任意多边形 (展开平面) -> ShapeGeometry 三角化 -> 弯到弧上
    const appendShapePoly = (poly: IPoint2d[]) => {
      if (poly.length < 3) return;

      const shape = new THREE.Shape();
      poly.forEach((p, idx) => {
        if (idx === 0) shape.moveTo(p.x, p.y);
        else shape.lineTo(p.x, p.y);
      });

      const geo = new THREE.ShapeGeometry(shape);
      const pos = geo.attributes.position;
      const idx = geo.index;
      if (!idx) {
        geo.dispose();
        return;
      }

      const baseIdx = positions.length / 3;
      for (let i = 0; i < pos.count; i++) {
        pushVertex(pos.getX(i), pos.getY(i));
      }
      // 翻转每个三角形 winding (a, b, c) -> (a, c, b)
      for (let i = 0; i < idx.count; i += 3) {
        indices.push(baseIdx + idx.getX(i), baseIdx + idx.getX(i + 2), baseIdx + idx.getX(i + 1));
      }
      geo.dispose();
    };

    for (let i = 0; i < segCount; i++) {
      const u0 = i * stripWidth;
      const u1 = u0 + stripWidth;
      const hasHole = holeURanges.some((r) => r.minU < u1 && r.maxU > u0);

      if (!hasHole) {
        appendSimpleStrip(u0, u1);
        continue;
      }

      // 带 hole 的 strip: 用布尔差集得到剩余多边形, 每个独立三角化
      const rectPoly: IPoint2d[] = [
        { x: u0, y: bottomZ },
        { x: u1, y: bottomZ },
        { x: u1, y: topZ },
        { x: u0, y: topZ }
      ];
      const remaining = booleanPolygon({
        main: [rectPoly],
        assistant: holesUV,
        type: BooleanPolygonType.Difference
      });
      remaining.forEach((poly) => appendShapePoly(poly));
    }

    if (positions.length === 0) return;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    const material = createMaterial(faceData.materialInfo.id);
    // const material = new THREE.MeshBasicMaterial({ color: 0x00ff00, wireframe: true });
    return new THREE.Mesh(geometry, material);
  }

  updateWithoutDestroy(faceData: IFaceData) {
    if (!this.canUpdate) return;

    if (this._planeMesh) {
      const material = createMaterial(faceData.materialInfo.id);
      this._planeMesh.material = material;
    }
  }
}
